import type { JsonSchemaType } from '@gmn-portal/backend';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';
import {
  objectCategoryKeys,
  useCreateObjectCategory,
  useObjectCategory,
  useUpdateObjectCategory,
} from '@/core/api';
import { useAuth } from '@/core/auth';
import { useIntl } from '@/core/i18n/use-intl';
import { PageHeader } from '@/shared/components/layout/page-header';
import {
  type FieldError,
  MetadataBuilder,
  MetadataFormPreview,
  parseMetadataApiErrors,
} from '@/shared/components/metadata/metadata-builder';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Textarea,
} from '@/shared/components/ui';

const categoryDetailsSchema = z.object({
  name: z.string().min(1, 'Required'),
  description: z.string().optional(),
});

type CategoryDetailsFormValues = z.infer<typeof categoryDetailsSchema>;

// Default empty schema for new categories
const defaultMetadataSchema: JsonSchemaType = {
  type: 'object',
  groups: [],
  properties: {},
  required: [],
  propertyOrder: [],
};

export function ManageObjectCategory() {
  const { t } = useIntl();
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const createCategory = useCreateObjectCategory();
  const updateCategory = useUpdateObjectCategory();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;

  const { data: category, isLoading } = useObjectCategory(id || '', {
    disabled: !id,
  });

  const isGmnAdmin = currentUser?.role === 'gmn-admin';
  const formDisabled = isLoading || !isGmnAdmin;

  const [isUpdating, setIsUpdating] = useState(false);
  const [metadataSchema, setMetadataSchema] = useState<JsonSchemaType>(defaultMetadataSchema);
  const [fieldErrors, setFieldErrors] = useState<FieldError[]>([]);

  // Form for basic category details
  const detailsForm = useForm<CategoryDetailsFormValues>({
    resolver: zodResolver(categoryDetailsSchema),
    defaultValues: {
      name: '',
      description: '',
    },
  });

  // Reset forms when category data loads
  useEffect(() => {
    if (isEditMode && category && !isLoading) {
      detailsForm.reset({
        name: category.name,
        description: category.description || '',
      });
      setMetadataSchema((category.metadataSchema as JsonSchemaType) || defaultMetadataSchema);
    }
  }, [category, isEditMode, isLoading, detailsForm]);

  const handleSubmit = async () => {
    if (!isGmnAdmin) return;

    // Validate details form
    const detailsValid = await detailsForm.trigger();
    if (!detailsValid) {
      toast.error(t('validation.pleaseCorrectErrors'));
      return;
    }

    const detailsData = detailsForm.getValues();

    setIsUpdating(true);
    setFieldErrors([]); // Clear previous errors

    try {
      if (isEditMode) {
        await updateCategory.mutateAsync({
          id,
          data: {
            name: detailsData.name,
            description: detailsData.description,
            metadataSchema,
          },
        });
      } else {
        const created = await createCategory.mutateAsync({
          name: detailsData.name,
          description: detailsData.description,
          metadataSchema,
        });
        // Navigate to the edit page for the newly created category
        navigate(`/gmn/settings/object-categories/${created.doc.id}`, {
          replace: true,
        });
      }

      await queryClient.invalidateQueries({ queryKey: objectCategoryKeys.all });
      toast.success(
        isEditMode
          ? t('objectCategories.categoryUpdatedSuccessfully')
          : t('objectCategories.categoryCreatedSuccessfully')
      );
    } catch (error) {
      // Parse field-specific errors
      const parsedErrors = parseMetadataApiErrors(error, (key) => t(key));
      setFieldErrors(parsedErrors);

      // Handle remaining errors with toasts
      const payloadError = error as {
        response?: {
          errors?: Array<{
            data?: {
              errors?: Array<{
                code: string;
                message: string;
              }>;
            };
            message?: string;
          }>;
        };
      };

      if (payloadError.response?.errors) {
        payloadError.response.errors.forEach((err) => {
          if (err.data?.errors) {
            err.data.errors.forEach((apiError) => {
              // Only show toast for errors that weren't mapped to fields
              if (
                ![
                  'NewRequiredFieldAdditionNotAllowed',
                  'ExistingFieldMadeRequiredWithMissingValues',
                  'FieldTypeChangeNotAllowed',
                ].includes(apiError.code)
              ) {
                toast.error(t('metadata.metadataUpdateFailed'), {
                  description: apiError.message,
                });
              }
            });
          } else if (err.message) {
            toast.error(t('errors.unknownError'), {
              description: err.message,
            });
          }
        });
      } else if (parsedErrors.length === 0) {
        toast.error(t('errors.unknownError'));
      }
    }

    setIsUpdating(false);
  };

  // Clear errors when schema changes
  const handleSchemaChange = (schema: JsonSchemaType) => {
    setMetadataSchema(schema);
    if (fieldErrors.length > 0) {
      setFieldErrors([]);
    }
  };

  const getPageTitle = () => {
    return isEditMode ? t('objectCategories.editCategory') : t('objectCategories.newCategory');
  };

  return (
    <>
      <PageHeader
        title={getPageTitle()}
        backHref="/gmn/settings/object-categories"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate('/gmn/settings/object-categories')}>
              {t('common.discard')}
            </Button>
            <Button onClick={handleSubmit} disabled={!isGmnAdmin || isUpdating}>
              <Save className="size-4 mr-2" />
              {isUpdating ? t('common.saving') : t('common.save')}
            </Button>
          </div>
        }
      />

      <div className="space-y-6">
        {/* Category Details Card */}
        <Card>
          <CardHeader>
            <CardTitle>{t('objectCategories.categoryDetails')}</CardTitle>
            <CardDescription>{t('objectCategories.categoryDetailsDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...detailsForm}>
              <div className="space-y-4">
                <FormField
                  control={detailsForm.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>{t('common.name')}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          disabled={formDisabled}
                          placeholder={t('objectCategories.namePlaceholder')}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={detailsForm.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('common.description')}</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          disabled={formDisabled}
                          placeholder={t('objectCategories.descriptionPlaceholder')}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </Form>
          </CardContent>
        </Card>

        {/* Metadata Schema Card - New Builder with Groups and Preview */}
        <Card>
          <CardHeader>
            <CardTitle>{t('objectCategories.metadataSchema')}</CardTitle>
            <CardDescription>{t('objectCategories.metadataSchemaDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 min-h-[500px]">
              <MetadataBuilder
                value={metadataSchema}
                onChange={handleSchemaChange}
                disabled={!isGmnAdmin || isLoading}
                className="border-0 rounded-none shadow-none lg:border-r"
                fieldErrors={fieldErrors}
                header={false}
              />
              <MetadataFormPreview
                schema={metadataSchema}
                className="border-0 rounded-none shadow-none hidden lg:flex"
                header={false}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
